import { useRef } from "react";
import moment, { Moment } from "moment";

import { TextFieldProps } from "@mui/material";
import {
  DateTimeValidationError,
  PickerChangeHandlerContext,
} from "@mui/x-date-pickers/models";

type UseDateTimePickerKeyboardCommitProps = {
  committedValue: Moment | null;
  draftValue: Moment | null;
  onCommit: (date: Moment | null) => void;
  onBlur?: TextFieldProps["onBlur"];
  onKeyDown?: TextFieldProps["onKeyDown"];
};

function useDateTimePickerKeyboardCommit(
  props: UseDateTimePickerKeyboardCommitProps
) {
  const {
    committedValue,
    draftValue,
    onCommit,
    onBlur,
    onKeyDown,
  } = props;

  const validationErrorRef = useRef<DateTimeValidationError>(null);
  const actionBarInteractionRef = useRef(false);

  const resetCommitState = () => {
    validationErrorRef.current = null;
    actionBarInteractionRef.current = false;
  };

  const recordValidation = (
    context: PickerChangeHandlerContext<DateTimeValidationError>
  ) => {
    validationErrorRef.current = context.validationError;
  };

  const commitDraft = () => {
    const isUnchanged = draftValue
      ? !!committedValue && draftValue.isSame(committedValue)
      : committedValue === null;

    if (
      isUnchanged ||
      validationErrorRef.current ||
      (draftValue && !draftValue.isValid())
    ) {
      return;
    }

    onCommit(draftValue ? moment(draftValue) : null);
  };

  const handleBlur: NonNullable<TextFieldProps["onBlur"]> = (event) => {
    onBlur?.(event);

    const nextFocusedElement = event.relatedTarget;
    const focusStayedInPicker =
      nextFocusedElement instanceof Element &&
      !!nextFocusedElement.closest('[role="dialog"]');

    if (
      event.defaultPrevented ||
      actionBarInteractionRef.current ||
      focusStayedInPicker
    ) {
      return;
    }

    commitDraft();
  };

  const handleKeyDown: NonNullable<TextFieldProps["onKeyDown"]> = (event) => {
    onKeyDown?.(event);

    if (
      event.defaultPrevented ||
      event.key !== "Enter" ||
      !(event.target instanceof HTMLInputElement)
    ) {
      return;
    }

    event.preventDefault();
    commitDraft();
  };

  const markActionBarInteraction = () => {
    actionBarInteractionRef.current = true;
  };

  return {
    handleBlur,
    handleKeyDown,
    markActionBarInteraction,
    recordValidation,
    resetCommitState,
  };
}

export default useDateTimePickerKeyboardCommit;
